import { Card } from '@/components/ui/card';
import { Users, Building2, MapPin, Activity } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useContentPlays } from '@/lib/hooks/use-content-plays';
import { supabase } from '@/lib/supabase';
import { Database } from '@/types/supabase';
import { DonutChart } from '@/components/charts/donut-chart';
import { TypesBarChart } from '@/components/charts/types-bar-chart';
import { UsersOverTimeChart } from '@/components/charts/users-over-time-chart';
import AudioCard from '@/components/audio-card';
import { useNavigate } from 'react-router-dom';
import { getDeepLink } from '@/lib/constants';
import { useAdminPreview } from '@/lib/admin-preview-context';

const TIME_FRAMES = [
  { value: '24uur', label: '24 uur' },
  { value: '7dagen', label: '7 dagen' },
  { value: '30dagen', label: '30 dagen' },
  { value: '3maanden', label: '3 maanden' },
  { value: 'altijd', label: 'alles' },
] as const;

type TimeFrame = (typeof TIME_FRAMES)[number]['value'];

export default function AdminDashboard() {
  const { isPreviewMode } = useAdminPreview();

  const navigate = useNavigate();
  const [globalTimeFrame, setGlobalTimeFrame] = useState<TimeFrame>('30dagen');
  const timeFilters = useMemo(
    () => getTimeFilters(globalTimeFrame),
    [globalTimeFrame],
  );

  // Fetch hoofdhartuser IDs for filtering in preview mode
  const [hnhUserIds, setHnhUserIds] = useState<string[]>([]);

  useEffect(() => {
    if (!isPreviewMode) {
      setHnhUserIds([]);
      return;
    }
    supabase.from('profile_role').select('user_id').eq('role', 'hoofdhartuser')
      .then(({ data }) => { if (data) setHnhUserIds(data.map((r) => r.user_id)); });
  }, [isPreviewMode]);

  const { contentPlays } = useContentPlays({
    excludeAdmins: true,
    ...timeFilters,
    ...(isPreviewMode && hnhUserIds.length > 0 ? { userIds: hnhUserIds } : {}),
  });

  // Team meditation stats
  const [teamMeditationCount, setTeamMeditationCount] = useState(0);

  useEffect(() => {
    async function fetchMeditationCount() {
      // In preview mode, query samen_meditations instead
      const table = isPreviewMode ? 'samen_meditations' : 'team_meditations';

      let query = supabase
        .from(table)
        .select('id', { count: 'exact', head: true });

      if (timeFilters.startTime) {
        query = query.gte('scheduled_at', timeFilters.startTime.toISOString());
      }
      if (timeFilters.endTime) {
        query = query.lte('scheduled_at', timeFilters.endTime.toISOString());
      }

      const { count } = await query;
      setTeamMeditationCount(count ?? 0);
    }
    fetchMeditationCount();
  }, [timeFilters.startTime, timeFilters.endTime, isPreviewMode]);

  // Fetch user statistics
  /*
  useEffect(() => {
    async function fetchStats() {
      try {
        // Get total user count
        const { count: userCountResult, error: userError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        if (!userError && userCountResult !== null) {
          setUserCount(userCountResult);
        }

        // Get unique organizations count and details
        const { data: orgs, error: orgError } = await supabase
          .from('profiles')
          .select('organisatie')
          .not('organisatie', 'is', null);

        if (!orgError && orgs) {
          const uniqueOrgs = new Set(orgs.map((o) => o.organisatie));
          setOrgCount(uniqueOrgs.size);

          // Get user count per organization
          const orgDetails = {};
          for (const org of uniqueOrgs) {
            const { count: orgUserCount, error: countError } = await supabase
              .from('profiles')
              .select('*', { count: 'exact', head: true })
              .eq('organisatie', org);

            if (!countError) {
              orgDetails[org] = orgUserCount;
            }
          }
        }

        // Get unique teams count and details
        const { data: teams, error: teamError } = await supabase
          .from('profiles')
          .select('team, organisatie')
          .not('team', 'is', null);

        if (!teamError && teams) {
          // Count unique teams (team + organization combinations)
          const uniqueTeamOrgs = new Set<string>();
          teams.forEach((t) => {
            const teamOrgKey = `${t.team}|${t.organisatie || 'none'}`;
            uniqueTeamOrgs.add(teamOrgKey);
          });
          setTeamCount(uniqueTeamOrgs.size);

          // Get user count per team
          const teamDetails = {};
          for (const teamOrgKey of uniqueTeamOrgs) {
            const [team, org] = (teamOrgKey as string).split('|');

            const query = supabase
              .from('profiles')
              .select('*', { count: 'exact', head: true })
              .eq('team', team);

            if (org !== 'none') {
              query.eq('organisatie', org);
            } else {
              query.is('organisatie', null);
            }

            const { count: teamUserCount, error: countError } = await query;

            if (!countError) {
              teamDetails[teamOrgKey] = teamUserCount;
            }
          }
        }

        // Get active users
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        // Currently active (last 15 minutes)
        const { count: activeNow, error: activeNowError } = await supabase
          .from('content_plays')
          .select('user_id', { count: 'exact', head: true })
          .gt(
            'played_at',
            new Date(now.getTime() - 15 * 60 * 1000).toISOString(),
          );

        // Active today
        const { data: activeToday, error: activeTodayError } = await supabase
          .from('content_plays')
          .select('user_id')
          .gt('played_at', oneDayAgo.toISOString());

        // Active this week
        const { data: activeWeek, error: activeWeekError } = await supabase
          .from('content_plays')
          .select('user_id')
          .gt('played_at', oneWeekAgo.toISOString());

        // Active this month
        const { data: activeMonth, error: activeMonthError } = await supabase
          .from('content_plays')
          .select('user_id')
          .gt('played_at', oneMonthAgo.toISOString());

        setActiveUsers({
          nu: activeNow || 0,
          dag: activeToday
            ? new Set(activeToday.map((a) => a.user_id)).size
            : 0,
          week: activeWeek ? new Set(activeWeek.map((a) => a.user_id)).size : 0,
          maand: activeMonth
            ? new Set(activeMonth.map((a) => a.user_id)).size
            : 0,
        });

        // Get active users per organization
        if (activeMonth && activeMonth.length > 0) {
          const activeUserIds = new Set(activeMonth.map((a) => a.user_id));

          // Get organization for each active user
          const { data: activeUserOrgs, error: activeOrgsError } =
            await supabase
              .from('profiles')
              .select('id, organisatie')
              .in('id', Array.from(activeUserIds));

          if (!activeOrgsError && activeUserOrgs) {
            // Count active users per organization
            const activePerOrg = {};
            activeUserOrgs.forEach((user) => {
              const org = user.organisatie || 'Individual';
              activePerOrg[org] = (activePerOrg[org] || 0) + 1;
            });
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    }

    fetchStats();
  }, []);
  */

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">{isPreviewMode ? 'HnH Dashboard' : 'Activiteit Dashboard'}</h1>

        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            *Admin activiteit is uitgesloten
          </p>
          <div className="relative w-fit">
            <span className="text-sm" style={{ paddingRight: '10px' }}>
              Tijdsperiode:
            </span>
            <select
              value={globalTimeFrame}
              onChange={(e) => setGlobalTimeFrame(e.target.value as TimeFrame)}
              className="h-10 rounded-md border border-input bg-background px-4 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 appearance-none pr-10"
            >
              {TIME_FRAMES.map((timeFrame) => (
                <option key={timeFrame.value} value={timeFrame.value}>
                  {timeFrame.label}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 4.5L6 8L9.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Actieve Gebruikers</h3>
            <Activity className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-3xl font-bold">{contentPlays.activeUsers}</p>
        </Card>

        <Card className="p-6 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">{isPreviewMode ? 'Samen Meditaties' : 'Team Meditaties'}</h3>
            <Users className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-3xl font-bold">{teamMeditationCount}</p>
          <p className="text-sm text-muted-foreground">sessies gepland</p>
        </Card>

        {/* <Card className="p-6 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Totaal aantal gebruikers</h3>
            <Users className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-3xl font-bold">{userCount}</p>
        </Card>

        <Card className="p-6 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Organisaties</h3>
            <Building2 className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-3xl font-bold">{orgCount}</p>
        </Card>

        <Card className="p-6 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Teams / Locaties</h3>
            <MapPin className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-3xl font-bold">{teamCount}</p>
        </Card> */}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Totaal Beluisterd</h3>
          <div className="w-full h-[300px] relative">
            <div className="w-full h-full flex items-center justify-center">
              <DonutChart contentPlays={contentPlays} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Totaal Sessies</h3>
          <div className="w-full h-[300px] relative overflow-y-auto">
            <TypesBarChart contentPlays={contentPlays} />
          </div>
        </Card>

        {/* Most Played Content Section */}
        {contentPlays.topPlayed.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-medium mb-4">Meest Beluisterd</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contentPlays.topPlayed.slice(0, 6).map((topPlay) => (
                <AudioCard
                  key={topPlay.content.id}
                  content={topPlay.content}
                  hideDescription={true}
                  onClick={() => {
                    navigate(
                      getDeepLink(
                        topPlay.content.content_type,
                        topPlay.content.id,
                      ),
                    );
                  }}
                />
              ))}
            </div>
          </Card>
        )}

        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">
            Gebruikers Activiteit
          </h3>
          <UsersOverTimeChart
            startTime={timeFilters.startTime}
            endTime={timeFilters.endTime}
            timeFrame={globalTimeFrame}
            userIds={isPreviewMode && hnhUserIds.length > 0 ? hnhUserIds : undefined}
          />
        </Card>
      </div>
    </div>
  );
}

/**
 * Maps a global time frame to start and end time Date objects in UTC
 */
function getTimeFilters(globalTimeFrame: TimeFrame): {
  startTime: Date | null;
  endTime: Date;
} {
  const now = new Date();
  const endTime = now;
  let startTime: Date | null = null;

  switch (globalTimeFrame) {
    case '24uur':
      startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      break;
    case '7dagen':
      startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30dagen':
      startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '3maanden':
      startTime = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case 'altijd':
      startTime = null; // No start time = all time
      break;
    default: {
      const _exhaustiveCheck: never = globalTimeFrame;
      console.error(`Unexpected globalTimeFrame value: ${_exhaustiveCheck}`);
      startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    }
  }

  return { startTime, endTime };
}
